/**
 * AdminPanel — Painel de gestão para super_admin e org_admin
 * Story SAAS-3.4 + SAAS-3.5 / EPIC-SAAS-001
 *
 * Tabs:
 *   - Organizations (super_admin only) — CRUD de empresas clientes
 *   - Lojas — CRUD de lojas (escopado por role)
 *   - Users — CRUD de usuários (escopado por role)
 *   - Stats — métricas escopadas
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Building2, Store, Users, BarChart3, Plus, Edit, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  useOrganizations,
  useCreateOrganization,
  useUpdateOrganization,
  useLojas,
  useCreateLoja,
  useUpdateLoja,
  useOrgUsers,
  useCreateOrgUser,
  useUpdateOrgUser,
  useDeleteOrgUser,
  useOrgStats,
  type OrgTenant,
  type OrgUser,
} from "@/hooks/useOrgAdmin";

// ─── Componente principal ─────────────────────────────────────────────

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isSuperAdmin, isOrgAdmin, currentOrganizationId, isLoading: tenantLoading } = useTenant();

  const [orgFilterId, setOrgFilterId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth", { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!tenantLoading && !isSuperAdmin && !isOrgAdmin) {
      navigate("/", { replace: true });
    }
  }, [tenantLoading, isSuperAdmin, isOrgAdmin, navigate]);

  if (authLoading || tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // org_admin: filtro fica fixo na própria org (não tem opção)
  // super_admin: pode filtrar por organization
  const effectiveOrgFilter = isSuperAdmin ? orgFilterId : currentOrganizationId ?? undefined;

  return (
    <SidebarProvider>
      <AppSidebar />
      <main className="flex-1 overflow-auto">
        <div className="container mx-auto p-6 max-w-7xl">
          <div className="mb-6">
            <h1 className="text-3xl font-bold tracking-tight">Painel Administrativo</h1>
            <p className="text-muted-foreground mt-1">
              {isSuperAdmin
                ? "Gerencie empresas, lojas e usuários do SaaS"
                : "Gerencie as lojas e usuários da sua empresa"}
            </p>
          </div>

          <Tabs defaultValue={isSuperAdmin ? "organizations" : "lojas"} className="space-y-4">
            <TabsList>
              {isSuperAdmin && (
                <TabsTrigger value="organizations">
                  <Building2 className="h-4 w-4 mr-2" /> Empresas
                </TabsTrigger>
              )}
              <TabsTrigger value="lojas">
                <Store className="h-4 w-4 mr-2" /> Lojas
              </TabsTrigger>
              <TabsTrigger value="users">
                <Users className="h-4 w-4 mr-2" /> Usuários
              </TabsTrigger>
              <TabsTrigger value="stats">
                <BarChart3 className="h-4 w-4 mr-2" /> Estatísticas
              </TabsTrigger>
            </TabsList>

            {isSuperAdmin && (
              <TabsContent value="organizations">
                <OrganizationsTab onSelectOrg={setOrgFilterId} selectedOrgId={orgFilterId} />
              </TabsContent>
            )}

            <TabsContent value="lojas">
              <LojasTab orgFilter={effectiveOrgFilter} />
            </TabsContent>

            <TabsContent value="users">
              <UsersTab orgFilter={effectiveOrgFilter} />
            </TabsContent>

            <TabsContent value="stats">
              <StatsTab orgFilter={effectiveOrgFilter} />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </SidebarProvider>
  );
}

// ─── Tab: Organizations (super_admin only) ────────────────────────────

function OrganizationsTab({
  onSelectOrg,
  selectedOrgId,
}: {
  onSelectOrg: (id: string | undefined) => void;
  selectedOrgId: string | undefined;
}) {
  const { data: orgs = [], isLoading } = useOrganizations();
  const createMut = useCreateOrganization();
  const updateMut = useUpdateOrganization();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<OrgTenant | null>(null);
  const [form, setForm] = useState({ slug: "", nome: "" });

  const openCreate = () => {
    setEditing(null);
    setForm({ slug: "", nome: "" });
    setDialogOpen(true);
  };

  const openEdit = (org: OrgTenant) => {
    setEditing(org);
    setForm({ slug: org.slug, nome: org.nome });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, nome: form.nome });
        toast.success("Empresa atualizada");
      } else {
        await createMut.mutateAsync({ slug: form.slug, nome: form.nome });
        toast.success("Empresa criada");
      }
      setDialogOpen(false);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const toggleAtivo = async (org: OrgTenant) => {
    try {
      await updateMut.mutateAsync({ id: org.id, ativo: !org.ativo });
      toast.success(`Empresa ${!org.ativo ? "ativada" : "desativada"}`);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Empresas Clientes (Organizations)</CardTitle>
          <CardDescription>Empresas que utilizam o SaaS. Clique em uma para filtrar lojas e users.</CardDescription>
        </div>
        <Button onClick={openCreate} size="sm">
          <Plus className="h-4 w-4 mr-2" /> Nova Empresa
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orgs.map(org => (
                <TableRow
                  key={org.id}
                  className={`cursor-pointer ${selectedOrgId === org.id ? "bg-muted/50" : ""}`}
                  onClick={() => onSelectOrg(selectedOrgId === org.id ? undefined : org.id)}
                >
                  <TableCell className="font-medium">{org.nome}</TableCell>
                  <TableCell className="font-mono text-xs">{org.slug}</TableCell>
                  <TableCell>
                    <Badge variant={org.ativo ? "default" : "secondary"}>
                      {org.ativo ? "Ativa" : "Inativa"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); openEdit(org); }}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Switch
                      checked={org.ativo}
                      onClick={(e) => e.stopPropagation()}
                      onCheckedChange={() => toggleAtivo(org)}
                      className="ml-2"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Empresa" : "Nova Empresa"}</DialogTitle>
            <DialogDescription>
              {editing ? "Atualize o nome da empresa" : "Cadastre uma nova empresa cliente do SaaS"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!editing && (
              <div>
                <Label htmlFor="org-slug">Slug</Label>
                <Input
                  id="org-slug"
                  placeholder="ex: minha-empresa"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
                />
                <p className="text-xs text-muted-foreground mt-1">apenas letras minúsculas, números e hífens</p>
              </div>
            )}
            <div>
              <Label htmlFor="org-nome">Nome</Label>
              <Input
                id="org-nome"
                placeholder="ex: Minha Empresa Ltda"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={createMut.isPending || updateMut.isPending}>
              {(createMut.isPending || updateMut.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Tab: Lojas ───────────────────────────────────────────────────────

function LojasTab({ orgFilter }: { orgFilter: string | undefined }) {
  const { isSuperAdmin } = useTenant();
  const { data: lojas = [], isLoading } = useLojas(orgFilter);
  const { data: orgs = [] } = useOrganizations();
  const createMut = useCreateLoja();
  const updateMut = useUpdateLoja();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<OrgTenant | null>(null);
  const [form, setForm] = useState({ slug: "", nome: "", parentTenantId: "", gestaoclickLojaId: "" });

  const openCreate = () => {
    setEditing(null);
    setForm({ slug: "", nome: "", parentTenantId: orgFilter || "", gestaoclickLojaId: "" });
    setDialogOpen(true);
  };

  const openEdit = (loja: OrgTenant) => {
    setEditing(loja);
    setForm({
      slug: loja.slug,
      nome: loja.nome,
      parentTenantId: loja.parentTenantId || "",
      gestaoclickLojaId: loja.gestaoclickLojaId || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editing) {
        await updateMut.mutateAsync({
          id: editing.id,
          nome: form.nome,
          gestaoclickLojaId: form.gestaoclickLojaId || null,
        });
        toast.success("Loja atualizada");
      } else {
        const input: any = { slug: form.slug, nome: form.nome };
        if (form.gestaoclickLojaId) input.gestaoclickLojaId = form.gestaoclickLojaId;
        if (isSuperAdmin && form.parentTenantId) input.parentTenantId = form.parentTenantId;
        await createMut.mutateAsync(input);
        toast.success("Loja criada");
      }
      setDialogOpen(false);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const toggleAtivo = async (loja: OrgTenant) => {
    try {
      await updateMut.mutateAsync({ id: loja.id, ativo: !loja.ativo });
      toast.success(`Loja ${!loja.ativo ? "ativada" : "desativada"}`);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const orgMap = new Map(orgs.map(o => [o.id, o]));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Lojas</CardTitle>
          <CardDescription>
            {isSuperAdmin
              ? orgFilter
                ? `Lojas de ${orgMap.get(orgFilter)?.nome ?? orgFilter}`
                : "Todas as lojas (cross-empresa)"
              : "Lojas da sua empresa"}
          </CardDescription>
        </div>
        <Button onClick={openCreate} size="sm" disabled={isSuperAdmin && !orgFilter}>
          <Plus className="h-4 w-4 mr-2" /> Nova Loja
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Slug</TableHead>
                {isSuperAdmin && <TableHead>Empresa</TableHead>}
                <TableHead>GestãoClick ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lojas.map(loja => (
                <TableRow key={loja.id}>
                  <TableCell className="font-medium">{loja.nome}</TableCell>
                  <TableCell className="font-mono text-xs">{loja.slug}</TableCell>
                  {isSuperAdmin && (
                    <TableCell className="text-xs">
                      {loja.parentTenantId ? orgMap.get(loja.parentTenantId)?.nome ?? "—" : "—"}
                    </TableCell>
                  )}
                  <TableCell className="font-mono text-xs">{loja.gestaoclickLojaId || "—"}</TableCell>
                  <TableCell>
                    <Badge variant={loja.ativo ? "default" : "secondary"}>
                      {loja.ativo ? "Ativa" : "Inativa"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(loja)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Switch
                      checked={loja.ativo}
                      onCheckedChange={() => toggleAtivo(loja)}
                      className="ml-2"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Loja" : "Nova Loja"}</DialogTitle>
            <DialogDescription>
              {editing ? "Atualize os dados da loja" : "Cadastre uma nova loja"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!editing && (
              <div>
                <Label>Slug</Label>
                <Input
                  placeholder="ex: minha-loja-rj"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
                />
              </div>
            )}
            <div>
              <Label>Nome</Label>
              <Input
                placeholder="ex: Minha Loja - Filial RJ"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
              />
            </div>
            {!editing && isSuperAdmin && (
              <div>
                <Label>Empresa (Organization)</Label>
                <Select
                  value={form.parentTenantId}
                  onValueChange={(v) => setForm({ ...form, parentTenantId: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    {orgs.filter(o => o.ativo).map(o => (
                      <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>GestãoClick Loja ID (opcional)</Label>
              <Input
                placeholder="ex: 99745"
                value={form.gestaoclickLojaId}
                onChange={(e) => setForm({ ...form, gestaoclickLojaId: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={createMut.isPending || updateMut.isPending}>
              {(createMut.isPending || updateMut.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Tab: Users ───────────────────────────────────────────────────────

function UsersTab({ orgFilter }: { orgFilter: string | undefined }) {
  const { isSuperAdmin } = useTenant();
  const { data: users = [], isLoading } = useOrgUsers(orgFilter);
  const { data: lojas = [] } = useLojas(orgFilter);
  const { data: orgs = [] } = useOrganizations();
  const createMut = useCreateOrgUser();
  const updateMut = useUpdateOrgUser();
  const deleteMut = useDeleteOrgUser();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<OrgUser | null>(null);
  const [form, setForm] = useState({
    email: "",
    password: "",
    nomeCompleto: "",
    role: "admin" as "admin" | "gerente" | "producao" | "org_admin",
    tenantId: "",
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ email: "", password: "", nomeCompleto: "", role: "admin", tenantId: "" });
    setDialogOpen(true);
  };

  const openEdit = (u: OrgUser) => {
    setEditing(u);
    setForm({
      email: u.userId,
      password: "",
      nomeCompleto: u.nomeCompleto,
      role: (u.role === "super_admin" ? "admin" : u.role) as any,
      tenantId: u.tenantId || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editing) {
        const updates: any = { userId: editing.userId, nomeCompleto: form.nomeCompleto, role: form.role };
        if (form.password) updates.password = form.password;
        if (form.tenantId) updates.tenantId = form.tenantId;
        await updateMut.mutateAsync(updates);
        toast.success("Usuário atualizado");
      } else {
        await createMut.mutateAsync({
          email: form.email,
          password: form.password,
          nomeCompleto: form.nomeCompleto,
          role: form.role,
          tenantId: form.tenantId,
        });
        toast.success("Usuário criado");
      }
      setDialogOpen(false);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (u: OrgUser) => {
    if (!confirm(`Desativar ${u.nomeCompleto}?`)) return;
    try {
      await deleteMut.mutateAsync(u.userId);
      toast.success("Usuário desativado");
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const tenantMap = new Map([...orgs, ...lojas].map(t => [t.id, t]));

  // Para org_admin: opções de tenant são lojas da org + a própria org (para criar outro org_admin)
  const targetTenantOptions = isSuperAdmin
    ? [...orgs.filter(o => o.ativo), ...lojas.filter(l => l.ativo)]
    : (orgFilter
        ? [
            ...orgs.filter(o => o.id === orgFilter && o.ativo),
            ...lojas.filter(l => l.parentTenantId === orgFilter && l.ativo),
          ]
        : []);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Usuários</CardTitle>
          <CardDescription>
            {isSuperAdmin
              ? orgFilter
                ? `Usuários de ${tenantMap.get(orgFilter)?.nome ?? orgFilter}`
                : "Todos os usuários (cross-empresa)"
              : "Usuários da sua empresa"}
          </CardDescription>
        </div>
        <Button onClick={openCreate} size="sm" disabled={!isSuperAdmin && !orgFilter}>
          <Plus className="h-4 w-4 mr-2" /> Novo Usuário
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">{u.nomeCompleto}</TableCell>
                  <TableCell className="text-xs">{u.userId}</TableCell>
                  <TableCell>
                    <Badge variant={u.role === "super_admin" ? "destructive" : u.role === "org_admin" ? "default" : "secondary"}>
                      {u.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {u.tenantId ? tenantMap.get(u.tenantId)?.nome ?? u.tenantId.slice(0, 8) : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={u.ativo ? "default" : "secondary"}>
                      {u.ativo ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(u)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDelete(u)}
                      disabled={u.role === "super_admin"}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Usuário" : "Novo Usuário"}</DialogTitle>
            <DialogDescription>
              {editing ? "Atualize dados do usuário" : "Cadastre novo usuário"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!editing && (
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            )}
            <div>
              <Label>Nome Completo</Label>
              <Input value={form.nomeCompleto} onChange={(e) => setForm({ ...form, nomeCompleto: e.target.value })} />
            </div>
            <div>
              <Label>{editing ? "Nova Senha (deixe vazio para não trocar)" : "Senha"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v: any) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin (loja)</SelectItem>
                  <SelectItem value="gerente">Gerente</SelectItem>
                  <SelectItem value="producao">Produção</SelectItem>
                  <SelectItem value="org_admin">Org Admin (toda a empresa)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              {/* Loja é atributo do pedido, não escopo de usuário (REGRAS §13.31):
                  todo usuário vive na organization e vê todas as lojas. O
                  servidor normaliza loja → organization; a lista só oferece
                  organizations para a escolha e o que fica gravado baterem. */}
              <Label>Empresa</Label>
              <Select value={form.tenantId} onValueChange={(v) => setForm({ ...form, tenantId: v })}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {targetTenantOptions
                    .filter(t => t.tipo === "organization")
                    .map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.nome} <span className="text-xs text-muted-foreground">({t.tipo})</span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={createMut.isPending || updateMut.isPending}>
              {(createMut.isPending || updateMut.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Tab: Stats ───────────────────────────────────────────────────────

function StatsTab({ orgFilter }: { orgFilter: string | undefined }) {
  const { data: stats, isLoading } = useOrgStats(orgFilter);

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin" />;
  if (!stats) return <p className="text-muted-foreground">Sem dados</p>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Estatísticas</CardTitle>
        <CardDescription>
          {orgFilter ? "Métricas da empresa selecionada" : "Métricas globais (todas as empresas)"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <pre className="bg-muted p-4 rounded text-xs overflow-auto">
          {JSON.stringify(stats, null, 2)}
        </pre>
      </CardContent>
    </Card>
  );
}
